import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColDef, GetRowIdParams, GridOptions, IsRowSelectable, RowNode } from "ag-grid-community";
import '../../../style/FinanceTable.css'
import { CircularProgress, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import {
    DeleteRounded,
    DescriptionRounded,
    DownloadRounded,
    InfoRounded,
    LockOpenRounded,
    LockRounded,
    LoopRounded,
    ScheduleSendRounded,
    SendRounded
} from "@mui/icons-material";
import ColouredChip from "../../../component/ColouredChip";
import { formatCurrency, formatDate } from "../../../utils/FormatUtils";
import { ID_BILLED, ID_ERROR, ID_PROCESSING, ID_PUBLISHED, ID_SENT, ID_TO_SEND } from "../constants/MobilityStoreStatuses";
import { COLOR_BILLED, COLOR_ERROR, COLOR_PUBLISHED, COLOR_SENT, COLOR_TO_SEND } from "../constants/MobilityStoreColors";
import IconButton from "@mui/material/IconButton";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { useSetRecoilState } from "recoil";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import { useMobilityStoreRepository } from "../../../api/repositories/MobilityStoreRepository";
import FinanceDialog from "../../../component/FinanceDialog";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { NumericSummary } from "../../../component/NumericSummary";
import EmptyState from "../../../../../util/component/EmptyState";
import { useParams } from "react-router-dom";
import {downloadFilePdf} from "./ReportTableUtil";
import {decode} from "../../../utils/Base64Utils";
const ReportPreviewTable = (props: any) => {

    const { t } = useTranslation('finance', { keyPrefix: 'mobility-store' });
    const { reprocessPrintout, deletePrintout, downloadPDF, updatePrintoutStatus, switchPrintoutManualBilling } = useMobilityStoreRepository();

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openSendDialog, setOpenSendDialog] = useState(false);
    const [printoutToSend, setPrintoutToSend] = useState('');
    const [reportToDelete, setReportToDelete] = useState({ id: '', msName: '', type: '' });
    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);
    const { getSummaryByReportId } = useMobilityStoreRepository();
    const { id } = useParams();

    const theme = useTheme();
    const gridRef = useRef<AgGridReact>(null);

    const columnDefs : ColDef[] = [
        {
            field: 'validation', headerName: '', headerTooltip: '',
            suppressSizeToFit: true, maxWidth: 65,
            sortable: false,
            checkboxSelection: true, showDisabledCheckboxes: true, headerCheckboxSelection: true,
            lockVisible: true, lockPinned: true,
        },
        {
            field: 'msName',
            headerName: t('tables.printout.ms-name'),
            headerTooltip: t('tables.printout.ms-name'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msSapId',
            headerName: t('tables.printout.ms-sap-id'),
            headerTooltip: t('tables.printout.ms-sap-id'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msVendorId',
            headerName: t('tables.printout.ms-vendor-id'),
            headerTooltip: t('tables.printout.ms-vendor-id'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msVendorName',
            headerName: t('tables.printout.ms-vendor-name'),
            headerTooltip: t('tables.printout.ms-vendor-name'),
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msVendorEmail',
            headerName: t('tables.printout.ms-vendor-email'),
            headerTooltip: t('tables.printout.ms-vendor-email'),
            valueFormatter: params => params.value || '---',
            minWidth: 200,
            unSortIcon: true,
        },
        {
            field: 'msType',
            headerName: t('tables.printout.ms-type'),
            headerTooltip: t('tables.printout.ms-type'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => params.value ? t('printout-ms-types.' + params.value) : '---'
        },
        {
            field: 'type',
            headerName: t('tables.printout.typology'),
            headerTooltip: t('tables.printout.typology'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t('printout-types.' + params.value)
        },
        {
            field: 'clusterName',
            headerName: t('tables.printout.cluster-name'),
            headerTooltip: t('tables.printout.cluster-name'),
            valueFormatter: params => params.value || '---',
            minWidth: 200,
            unSortIcon: true
        },
        {
            field: 'totalAmount',
            headerName: t('tables.printout.total-amount'),
            headerTooltip: t('tables.printout.total-amount'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatCurrency(params.value, 'EUR', 0.01)
        },
        {
            field: "invoice.invoiceNumber", headerName: t("tables.printout.invoiceNumber"),
            minWidth: 180,
            unSortIcon: true, sortable: true
        },
        {
            field: "invoice.sapInvoiceNumber", headerName: t("tables.printout.sapInvoiceNumber"),
            minWidth: 180,
            unSortIcon: true, sortable: true
        },
        {
            field: 'sentDate',
            headerName: t('tables.printout.published-date'),
            headerTooltip: t('tables.printout.published-date'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'status', headerName: t('tables.printout.status'), headerTooltip: t('tables.printout.status'),
            minWidth: 150, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_TO_SEND:
                        return {
                            component: () => <ColouredChip name={ID_TO_SEND} color={COLOR_TO_SEND}/>
                        };
                    case ID_PUBLISHED:
                        return {
                            component: () => <ColouredChip name={ID_PUBLISHED} color={COLOR_PUBLISHED}/>
                        };
                    case ID_BILLED:
                        return {
                            component: () => <ColouredChip name={ID_BILLED} color={COLOR_BILLED}/>
                        };
                    case ID_SENT:
                        return {
                            component: () => <ColouredChip name={ID_SENT} color={COLOR_SENT}/>
                        };
                    case ID_PROCESSING:
                        return {
                            component: () => (
                                    <Stack sx={{ alignItems: 'center', height: '100%' }} direction={'row'} spacing={1}>
                                        <Typography fontSize={'small'}>
                                            {t(`statuses.` + ID_PROCESSING)}
                                        </Typography>
                                        <CircularProgress size='15px' disableShrink={true}/>
                                    </Stack>
                            )
                        };
                    case ID_ERROR:
                        return {
                            component: () => <ColouredChip name={ID_ERROR} color={COLOR_ERROR}/>
                        };
                    default:
                        break;
                }
            }
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                return {
                    component: () =>
                            <Stack direction={"row"} justifyContent={"center"}>
                                <IconButton sx={{ color: theme.palette.accent.main }}
                                            disabled={params.data.status === ID_PROCESSING || params.data.status === ID_SENT || params.data.status === ID_BILLED}
                                            onClick={() => {
                                                switchPrintoutManualBilling(params.data.id).then(() => {
                                                    /* params.data.manualBilling = !params.data.manualBilling;
                                                     let rowNode= gridRef.current!.api.getRowNode(params.data.id);
                                                     if(rowNode) {
                                                         gridRef.current!.api.redrawRows({rowNodes: [rowNode]})
                                                     }
                                                     */
                                                    props.setUpdating(true);

                                                });
                                            }}>
                                    <Tooltip title={t('tooltips.manual_billing.' + params.data.manualBilling)} arrow placement="left">
                                        {params.data.manualBilling ? <LockRounded/> : <LockOpenRounded/>}
                                    </Tooltip>
                                </IconButton>
                                <FinanceDynamicActions>
                                    <IconButton disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        downloadPDF(params.data.id, "HTML").then((res) => {
                                            if (res?.result) {
                                                let pdfWindow = window.open("");
                                                pdfWindow!.document.write(decode(res.result));
                                            }
                                        });
                                    }}>
                                        <DescriptionRounded/>
                                    </IconButton>
                                    <IconButton disabled={params.data.status === ID_PROCESSING || params.data.status === ID_ERROR} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        downloadPDF(params.data.id, "PDF").then((res) => {
                                            if (res?.result) {
                                                downloadFilePdf(res?.result, "printout"+params.data.id+".pdf");
                                            }
                                        });
                                    }}>
                                        <DownloadRounded/>
                                    </IconButton>
                                    <IconButton disabled={params.data.status !== ID_TO_SEND && params.data.status !== ID_PUBLISHED} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        reprocessPrintout(params.data.id).then(() => {
                                            props.setUpdating(true);
                                            setSummaryUpdating(true);
                                        });
                                    }}>
                                        <LoopRounded/>
                                    </IconButton>
                                    <IconButton disabled={params.data.status !== ID_PUBLISHED} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        setOpenSendDialog(true);
                                        setPrintoutToSend(params.data.id);

                                    }}>
                                        <ScheduleSendRounded/>
                                    </IconButton>
                                    <IconButton disabled={params.data.status === ID_SENT || params.data.status === ID_BILLED} sx={{ color: theme.palette.accent.main }} onClick={() => {
                                        setOpenDeleteDialog(true);
                                        setReportToDelete({
                                            id: params.data.id,
                                            msName: params.data.msName,
                                            type: t('printout-types.' + params.data.type)
                                        });
                                    }}>
                                        <DeleteRounded/>
                                    </IconButton>
                                </FinanceDynamicActions>
                            </Stack>
                };
            },
            suppressFiltersToolPanel: true
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.report.overlays.no-rows.title') }
    };

    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (rowNode: RowNode) => {
            let status = rowNode.data.status;
            return status === ID_TO_SEND;
        };
    }, []);

    useEffect(() => {
        if (!!gridRef.current && !!gridRef.current.api) {
            gridRef.current.api.refreshCells({ force: true, columns: ['_actions'] });
        }
    }, [props.data])

    const toolbar = {
        renderInfo: () => (
                <Stack direction={'row'} spacing={3}>
                    <Typography variant={'body1'}>
                        {t('preview.top-label.key')}
                    </Typography>
                    <Typography variant={'body1'} fontWeight={'bold'}>
                        {t('preview.top-label.value', { creationDate: props?.date })}
                    </Typography>
                    <NumericSummary query={() => getSummaryByReportId(id!)} fields={[ID_PROCESSING, ID_TO_SEND, ID_PUBLISHED, ID_SENT, ID_BILLED, ID_ERROR]} i18nPath={"mobility-store.counters.printouts"}/>
                </Stack>
        ),
        renderActions: () => (
                <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{t('preview.buttons.publish.title') + '\n' + t('preview.buttons.publish.tooltip', {
                    selectedRows: props.selectedDataLength
                })}</div>} arrow>
                    <IconButton disabled={props.data.length === 0 || props.selectedDataLength === 0}
                                color={'accent'}
                                onClick={props.onPublishClick}>
                        <SendRounded/>
                    </IconButton>
                </Tooltip>
        )
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    return (
            <>
                <AgGridCustomTable
                        toolbar={toolbar}
                        noRowsOverlayComponent={noRowsOverlayComponent}
                        gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                        rowData={props.data} rowBuffer={0}
                        rowSelection={'multiple'} isRowSelectable={isRowSelectable}
                        suppressRowClickSelection
                        onGridReady={() => {
                            gridRef.current!.api?.sizeColumnsToFit();
                            gridRef.current!.api?.hideOverlay();
                        }}
                        onFirstDataRendered={() => {
                            gridRef.current!.api.selectAll();
                        }}
                        onRowSelected={(event: any) => {
                            props.onRowSelected(event.node.data.id, event.node.isSelected(), event.node.data);
                        }}
                        onSelectionChanged={() => {
                            props.setSelectedDataLength(gridRef.current!.api.getSelectedRows().length);
                        }}
                >
                </AgGridCustomTable>
                <FinanceDialog title={t('tables.printout.delete-dialog.title')}
                               content={t('tables.printout.delete-dialog.content', { msName: reportToDelete.msName, type: reportToDelete.type })}
                               cancelButton={t('tables.printout.delete-dialog.cancel')}
                               confirmButton={t('tables.printout.delete-dialog.confirm')}
                               open={openDeleteDialog}
                               titleIcon={<InfoRounded sx={{ color: 'accent.main' }}></InfoRounded>}
                               callback={() => {
                                   deletePrintout(reportToDelete.id).then(() => {
                                       props.setUpdating(true);
                                       setSummaryUpdating(true);
                                   })
                               }}
                               onClose={() => {
                                   setOpenDeleteDialog(false)
                               }}
                />
                <FinanceDialog title={t('tables.printout.send-dialog.title')}
                               content={t('tables.printout.send-dialog.content')}
                               cancelButton={t('tables.printout.send-dialog.cancel')}
                               confirmButton={t('tables.printout.send-dialog.confirm')}
                               open={openSendDialog}
                               titleIcon={<InfoRounded sx={{ color: 'accent.main' }}></InfoRounded>}
                               callback={() => {
                                   updatePrintoutStatus(printoutToSend).then((res) => {
                                       props.setUpdating(true);
                                       setSummaryUpdating(true);

                                   })
                               }}
                               onClose={() => {
                                   setOpenSendDialog(false)
                               }}
                />
            </>
    );
}

export default ReportPreviewTable;